import { LoadingButton } from '@mui/lab';
import { Autocomplete, Button, Grid, TextField, styled } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { namedByPlaceholder } from '~/components/forms/autocomplete-label';
import { useFolderContext, useFolderCopyPaths, type PathEntry } from '~/contexts';
import { assert } from '~/lib/assert';
import { Dialog, DialogTitle } from './components';
import { DialogActions, DialogContent } from './lib/styles';

const AutocompleteField = styled(TextField)(({ theme }) => ({
  '& label': { color: theme.palette.text.secondary },
}));

export interface MoveToFolderDialogProps {
  /** IDs of folders the user has permission to move items into */
  editableFolderIds: number[];
  onSubmit: (targetFolderId: number) => Promise<void>;
  loading?: boolean;
  onClose: () => void;
  open: boolean;
  i18nKey: string;
}

export const MoveToFolderDialog = ({
  editableFolderIds,
  onSubmit,
  loading = false,
  onClose,
  open,
  i18nKey,
}: MoveToFolderDialogProps) => {
  const { t } = useTranslation();
  const [targetFolder, setTargetFolder] = useState<PathEntry | null>(null);

  const { folderTree } = useFolderContext();
  const folders = useFolderCopyPaths(folderTree.id);

  const handleClose = useCallback(() => {
    setTargetFolder(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    assert(targetFolder != null, 'no folder selected!');
    await onSubmit(targetFolder.id);
    handleClose();
  }, [targetFolder, onSubmit, handleClose]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={handleClose}>{t(i18nKey)}</DialogTitle>

      <DialogContent>
        <Grid container direction="column" justifyContent="flex-start">
          <Autocomplete
            getOptionLabel={({ name }) => name}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(_event, value) => setTargetFolder(value)}
            options={folders.filter((x) => editableFolderIds.includes(x.id))}
            renderInput={(params) => (
              <AutocompleteField
                {...namedByPlaceholder(params, t('common.move_dialog.search_placeholder'))}
              />
            )}
            value={targetFolder}
          />
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <LoadingButton
          disabled={targetFolder == null}
          type="submit"
          color="primary"
          variant="contained"
          loading={loading}
          onClick={() => void handleSubmit()}
        >
          {t('common.move_dialog.move')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
